// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaGroup,
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  NavLink,
} from 'react-router-dom';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getImageUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatDateRange,
  getRegionByRegionSeasonId,
  getRegionSeasonByRegionSeasonId,
  isLatestRegionSeason,
} from 'common.helpers/dist/browser';
import {
  regattaStatusLabel,
} from 'common.language';
import {
  getLocalItemPath,
  getRegattaGroupName,
} from '../../../../helpers';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import LabelQuantity from '../../../common/LabelQuantity';
import LoadMoreTable from '../../../common/LoadMoreTable';
import RegistrationNo from '../../../common/RegistrationNo';
import NoData from '../../../common/NoData';

// Enums
import {
  statusLabelClass,
} from '../../../../enums';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

type $DataItem = $Model<'RegattaEntry'> & {
  Regatta: $Model<'Regatta'>;
  RegattaEntryCrewmembers: Array<$Model<'RegattaEntryCrewmember'>>;
  RegattaGroup?: $FullRegattaGroup;
};

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$DataItem>;
  loading: boolean;
  showItemsQuantity: number;
};

const step = 5;

class RegattaEntriesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      showItemsQuantity: 5,
    };
  }

  componentDidMount(): void {
    this.getData();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getProfileRegattaEntriesSectionData(undefined);

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  renderDataItemButtons(dataItem: $DataItem) {
    const {
      main,
      t,
    } = this.props;

    const path: string = getLocalItemPath({
      basePath: '/regatta',
      item: dataItem.Regatta,
      regions: main.regions,
    });

    return (
      <div className={'flex flex-col md:flex-row justify-end'}>
        {dataItem.Uid !== '' && (
          <NavLink
            className={'btn btn_success btn_outlined uppercase px-2 py-1 mb-3 md:mb-0 md:mr-3'}
            end
            title={t('Edit registration to regatta')}
            to={`${path}?regatta_entry_uid=${dataItem.Uid}`}
          >
            <em className={'la la-edit text-2xl'} />
          </NavLink>
        )}
        {dataItem.Regatta.PageEnabled === true && (
          <NavLink
            className={'btn btn_primary btn_outlined uppercase whitespace-nowrap px-2 md:px-3 py-1'}
            end
            title={t('Regatta page')}
            to={path}
          >
            <em className={'la la-flag text-2xl'} />
            <span className={'ml-2 hidden md:block'}>
              {t('Regatta page')}
            </span>
          </NavLink>
        )}
      </div>
    );
  }

  renderDateAndLocation(dataItem: $DataItem) {
    const {
      main,
      t,
    } = this.props;

    const {
      RegionSeasonId,
    } = dataItem.Regatta;

    const region = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId,
    });

    const regionSeason = getRegionSeasonByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId,
    });

    const date: string = formatDateRange({
      end: dataItem.Regatta.EndDate,
      shortDateFormat: region.ShortDateFormat,
      start: dataItem.Regatta.StartDate,
    });

    return (
      <div className={'text-sm text-gray-600'}>
        {!isLatestRegionSeason({
          regions: main.regions,
          RegionSeasonId,
        }) && (
          <span>
            {regionSeason.Name}
            <span className={'text-gray-400'}>
              {' | '}
            </span>
          </span>
        )}
        <span
          className={'mr-3'}
          title={t('Regatta date')}
        >
          {date}
        </span>
        <span
          className={'font-bold'}
          title={t('Regatta location')}
        >
          {dataItem.Regatta.Location}
        </span>
      </div>
    );
  }

  renderGroup(dataItem: $DataItem) {
    const {
      t,
    } = this.props;

    return (dataItem.RegattaGroup ? (
      <span
        className={'badge badge_outlined badge_secondary uppercase whitespace-nowrap mr-1 mb-1'}
        key={dataItem.RegattaGroup.Group.id}
      >
        {getRegattaGroupName(dataItem.RegattaGroup) || ''}
      </span>
    ) : t('Ungrouped'));
  }

  renderDataItem(dataItem: $DataItem) {
    const {
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: dataItem.Regatta.Image,
      size: 'min',
      undefinedImage: 'regatta.png',
    });

    return (
      <tr key={dataItem.id}>
        <td className={'hidden md:table-cell w-14'}>
          <img
            alt={t('Logo')}
            className={'max-h-10'}
            src={imageUrl}
          />
        </td>
        <td className={'md:hidden'}>
          <div className={'flex flex-row items-center mb-2'}>
            {dataItem.Regatta.Image && (
              <div className={'flex-none w-10 mr-2'}>
                <img
                  alt={t('Logo')}
                  src={imageUrl}
                />
              </div>
            )}
            <div className={'flex-1'}>
              <h3 className={'text-base font-semibold'}>
                {dataItem.Regatta.Name}
              </h3>
            </div>
          </div>
          <div className={'flex flex-row items-center mb-2'}>
            <div className={`badge badge_outlined badge_${statusLabelClass[dataItem.Regatta.Status]} uppercase py-1 my-auto mr-3`}>
              {t(regattaStatusLabel[dataItem.Regatta.Status])}
            </div>
            <div className={'text-xs'}>
              {this.renderDateAndLocation(dataItem)}
            </div>
          </div>
          <div className={'flex flex-row items-center'}>
            <div className={'leading-tight'}>
              <div className={'text-base mr-2'}>
                <RegistrationNo
                  item={dataItem}
                  regatta={dataItem.Regatta}
                />
              </div>
              <div className={'text-gray-600'}>
                {dataItem.Name || _.get(
                  dataItem,
                  'RegattaEntryCrewmembers[0].Name',
                  null,
                )}
              </div>
            </div>
          </div>
        </td>
        <td className={'hidden md:table-cell text-center'}>
          <div className={`badge badge_outlined badge_${statusLabelClass[dataItem.Regatta.Status]} uppercase py-1`}>
            {t(regattaStatusLabel[dataItem.Regatta.Status])}
          </div>
        </td>
        <td className={'hidden md:table-cell'}>
          <h3 className={'text-lg'}>{dataItem.Regatta.Name}</h3>
          {this.renderDateAndLocation(dataItem)}
        </td>
        <td className={'hidden md:table-cell'}>
          <div className={'leading-tight'}>
            <div className={'text-base mr-2'}>
              <RegistrationNo
                item={dataItem}
                regatta={dataItem.Regatta}
              />
            </div>
            <div className={'text-gray-600'}>
              {dataItem.Name || _.get(
                dataItem,
                'RegattaEntryCrewmembers[0].Name',
                null,
              )}
            </div>
          </div>
        </td>
        <td className={'hidden md:table-cell'}>
          {this.renderGroup(dataItem)}
        </td>
        <td className={'text-right'}>
          {this.renderDataItemButtons(dataItem)}
        </td>
      </tr>
    );
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
      showItemsQuantity,
    } = this.state;

    return (
      <Section
        id={webSections[webPages.profilePage].regattaEntries}
        title={t('My regattas')}
      >
        <Loader
          className={'overflow-x-auto'}
          fade
          loading={loading === true}
        >
          {data.length > 0 ? (
            <table className={'table table_hoverable w-full'}>
              <thead>
                <tr>
                  <th className={'hidden md:table-cell w-14'} />
                  <th className={'hidden md:table-cell text-center uppercase'}>
                    {t('Status')}
                  </th>
                  <th className={'hidden md:table-cell text-left uppercase'}>
                    {t('Regatta')}
                  </th>
                  <th className={'hidden md:table-cell text-left uppercase'}>
                    {t('Entry')}
                  </th>
                  <th className={'hidden md:table-cell text-left uppercase'}>
                    {t('Group')}
                  </th>
                  <th
                    className={'md:hidden text-right uppercase'}
                    colSpan={2}
                  >
                    <LabelQuantity value={data.length} />
                  </th>
                  <th className={'hidden md:table-cell text-right uppercase'}>
                    <LabelQuantity value={data.length} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {_.take(
                  data,
                  showItemsQuantity,
                ).map((dataItem: $DataItem) => this.renderDataItem(dataItem))}
                {data.length > showItemsQuantity && (
                  <LoadMoreTable
                    buttonLabel={t('Show older regattas')}
                    loadMoreAction={() => this.setState({
                      showItemsQuantity: showItemsQuantity + step,
                    })}
                  />
                )}
              </tbody>
            </table>
          ) : (
            <NoData label={t('The list is empty')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default RegattaEntriesSectionView;
